import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { EnvironmentService } from 'src/app/services/environment.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user: firebase.User | null = null;

  _isPro = false;

  public hasProOverride = false;

  public readonly FREE_LIMITATIONS = {
    maxTags: 10,
    availableColors: ['green', 'orange'],
  };

  set isPro(isPro: boolean) {
    this._isPro = isPro;
  }

  get isPro() {
    return this.isDemo
      ? true
      : this.environment.isDevelopmentBuild() &&
        this.isProForDevelopmentOverride
      ? this.isProForDevelopment
      : this._isPro || this.hasProOverride;
  }

  get isDemo() {
    return !this.user;
  }

  isProForDevelopmentOverride = false;
  isProForDevelopment = false;

  constructor(
    private afAuth: AngularFireAuth,
    private environment: EnvironmentService
  ) {
    this.afAuth.authState.subscribe((user) => {
      this.setProStatus(user);
      this.user = user;
    });
  }

  initializeAuth(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.afAuth.onAuthStateChanged((user: any) => {
        this.setProStatus(user);
        if (user) {
          this.user = user;
        } else {
          this.user = null;
        }
        resolve();
      }, reject);
    });
  }

  async loginWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> {
    const userCredential = await this.afAuth.signInWithEmailAndPassword(
      email,
      password
    );
    this.user = await this.afAuth.currentUser;
    this.setProStatus(this.user);

    window.location.reload();
    return userCredential;
  }

  async continueWithGoogle() {
    await this.continueWithProvider(new firebase.auth.GoogleAuthProvider());
  }

  private async continueWithProvider(provider: firebase.auth.AuthProvider) {
    try {
      const userCredential = await this.afAuth.signInWithPopup(provider);
      this.user = userCredential.user;
      this.setProStatus(this.user);
      window.location.reload();
    } catch (e) {
      console.error(e);
      throw new Error('Error signing in with provider');
    }
  }

  async logout(redirectPath?: string): Promise<void> {
    await this.afAuth.signOut();
    this.user = null;
    this.setProStatus(this.user);
    if (redirectPath) {
      window.location.pathname = redirectPath;
    } else {
      window.location.href = '/';
    }
  }

  setProStatus(user: firebase.User | null) {
    if (user) {
      user.getIdTokenResult().then((result) => {
        this.isPro = result.claims['stripeRole'] === 'pro';
      });
    } else {
      this.isPro = false;
    }
  }

  async sendPasswordResetEmail() {
    try {
      const email = this.user?.email;
      if (email) {
        await this.afAuth.sendPasswordResetEmail(email);
      } else {
        throw new Error('User not logged in');
      }
    } catch (e) {
      throw new Error('Error sending password reset email');
    }
  }

  async changeEmailAndLogOut(newEmail: string) {
    try {
      const user = await this.afAuth.currentUser;
      if (user) {
        await user.updateEmail(newEmail);
        this.afAuth.signOut();
        window.location.reload();
      } else {
        throw new Error('User not logged in');
      }
    } catch (e) {
      throw e;
    }
  }

  async sendEmailVerification() {
    try {
      const user = await this.afAuth.currentUser;
      if (user) {
        user.sendEmailVerification();
      } else {
        throw new Error('User not logged in');
      }
    } catch (e) {
      throw new Error('Error sending email verification');
    }
  }

  async deleteAccount() {
    try {
      const user = await this.afAuth.currentUser;
      if (user) {
        console.log('trying to delete');
        await user.delete();
        window.location.reload();
      } else {
        throw new Error('User not logged in');
      }
    } catch (e) {
      console.log('oops, something went wrong');
      throw e;
    }
  }

  isEmailSignup() {
    return this.user?.providerData[0]?.providerId === 'password';
  }

  async signupWithEmail(email: string, password: string) {
    if (!this.environment.environment.authenticationEnabled) {
      throw new Error('Authentication is not enabled for this build');
    }
    const credential = await this.afAuth.createUserWithEmailAndPassword(
      email,
      password
    );

    await credential.user?.sendEmailVerification();
  }

  isAuthenticationEnabled() {
    return this.environment.environment.authenticationEnabled;
  }
}
